import { Fragment, useEffect, useLayoutEffect, useRef, useState, type FormEvent, type TouchEvent } from 'react';
import { ArrowLeft, ArrowRight, BadgeDollarSign, ChevronDown, CircleMinus, ExternalLink, Newspaper, Search, TrendingDown, TrendingUp, X } from 'lucide-react';
import { fetchNews, searchNews, type NewsAsset } from '../lib/api/news';
import type { ApiNewsArticle } from '../types/api';

type NewsFilter = 'latest' | NewsAsset;
type MarkdownBlock = { type: 'heading'; text: string; level: 1 | 2 | 3 } | { type: 'paragraph'; text: string } | { type: 'list'; items: string[] };

const newsPageSize = 20;

const filters: Array<{ value: NewsFilter; label: string }> = [
  { value: 'latest', label: 'Latest' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'stock', label: 'Stocks' },
  { value: 'forex', label: 'Forex' },
];

export function NewsView({ active }: {
  active: boolean;
}) {
  const [activeFilter, setActiveFilter] = useState<NewsFilter>('latest');
  const [articles, setArticles] = useState<ApiNewsArticle[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<ApiNewsArticle | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newsLimit, setNewsLimit] = useState(newsPageSize);
  const [hasMoreNews, setHasMoreNews] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const viewRef = useRef<HTMLElement | null>(null);
  const listScrollTopRef = useRef(0);
  const pendingListScrollRestoreRef = useRef<number | null>(null);
  const pullRefreshRef = useRef({ startY: 0, active: false });
  const loadedRequestKeyRef = useRef('');
  const loadedBaseKeyRef = useRef('');

  useLayoutEffect(() => {
    if (selectedArticle || pendingListScrollRestoreRef.current === null) return undefined;
    const restoreTop = pendingListScrollRestoreRef.current;
    pendingListScrollRestoreRef.current = null;
    let frame = 0;
    let cancelled = false;
    const restore = () => {
      if (cancelled) return;
      if (viewRef.current) viewRef.current.scrollTop = restoreTop;
      frame += 1;
      if (frame < 3) requestAnimationFrame(restore);
    };
    requestAnimationFrame(restore);
    return () => {
      cancelled = true;
    };
  }, [selectedArticle]);

  useEffect(() => {
    if (!active) return;
    const searchAsset = activeFilter === 'crypto' || activeFilter === 'stock' || activeFilter === 'forex' ? activeFilter : undefined;
    const baseRequestKey = searchQuery ? `search:${searchAsset ?? 'all'}:${searchQuery}` : activeFilter;
    const requestKey = `${baseRequestKey}:${newsPageSize}`;
    if (hasLoaded && loadedRequestKeyRef.current === requestKey) return;
    let cancelled = false;
    setLoading(true);
    setLoadingMore(false);
    setError('');

    const request = searchQuery
      ? searchNews(searchQuery, { asset: searchAsset, limit: newsPageSize })
      : fetchNews({
          asset: activeFilter === 'crypto' || activeFilter === 'stock' || activeFilter === 'forex' ? activeFilter : undefined,
          limit: newsPageSize,
        });

    request
      .then((response) => {
        if (cancelled) return;
        const nextArticles = Array.isArray(response.articles) ? response.articles : [];
        setArticles(nextArticles);
        setNewsLimit(newsPageSize);
        setHasMoreNews(nextArticles.length >= newsPageSize);
        setHasLoaded(true);
        loadedRequestKeyRef.current = requestKey;
        loadedBaseKeyRef.current = baseRequestKey;
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load news');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [active, activeFilter, hasLoaded, searchQuery]);

  function selectFilter(filter: NewsFilter) {
    setActiveFilter(filter);
    setNewsLimit(newsPageSize);
    setHasMoreNews(true);
    scrollNewsToTop();
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextQuery = searchInput.trim();
    if (!nextQuery) return;
    setNewsLimit(newsPageSize);
    setHasMoreNews(true);
    setSearchQuery(nextQuery);
    setSelectedArticle(null);
    scrollNewsToTop();
  }

  function clearSearch() {
    setSearchInput('');
    setSearchQuery('');
    setNewsLimit(newsPageSize);
    setHasMoreNews(true);
    scrollNewsToTop();
  }

  function scrollNewsToTop() {
    requestAnimationFrame(() => {
      if (viewRef.current) viewRef.current.scrollTop = 0;
    });
  }

  async function loadMoreNews() {
    if (loadingMore || loading || !hasMoreNews) return;
    const nextLimit = Math.min(newsLimit + newsPageSize, 100);
    const searchAsset = activeFilter === 'crypto' || activeFilter === 'stock' || activeFilter === 'forex' ? activeFilter : undefined;
    setLoadingMore(true);
    setError('');
    try {
      const response = searchQuery
        ? await searchNews(searchQuery, { asset: searchAsset, limit: nextLimit })
        : await fetchNews({ asset: searchAsset, limit: nextLimit });
      const nextArticles = Array.isArray(response.articles) ? response.articles : [];
      setArticles((current) => appendNewArticles(current, nextArticles));
      setNewsLimit(nextLimit);
      setHasMoreNews(nextArticles.length >= nextLimit && nextLimit < 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load news');
    } finally {
      setLoadingMore(false);
    }
  }

  async function refreshNews() {
    if (refreshing || loading || loadingMore) return;
    const searchAsset = activeFilter === 'crypto' || activeFilter === 'stock' || activeFilter === 'forex' ? activeFilter : undefined;
    setRefreshing(true);
    setError('');
    try {
      const response = searchQuery
        ? await searchNews(searchQuery, { asset: searchAsset, limit: newsLimit })
        : await fetchNews({ asset: searchAsset, limit: newsLimit });
      const nextArticles = Array.isArray(response.articles) ? response.articles : [];
      setArticles(nextArticles);
      setHasMoreNews(nextArticles.length >= newsLimit && newsLimit < 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh news');
    } finally {
      setRefreshing(false);
      setPullDistance(0);
    }
  }

  function handleListTouchStart(event: TouchEvent<HTMLElement>) {
    const touch = event.touches[0];
    if (!touch || selectedArticle || viewRef.current?.scrollTop !== 0) return;
    pullRefreshRef.current = { startY: touch.clientY, active: true };
  }

  function handleListTouchMove(event: TouchEvent<HTMLElement>) {
    if (!pullRefreshRef.current.active || refreshing || loading || loadingMore) return;
    const touch = event.touches[0];
    if (!touch) return;
    const deltaY = touch.clientY - pullRefreshRef.current.startY;
    if (deltaY <= 0) return;
    setPullDistance(Math.min(64, deltaY * 0.45));
  }

  function handleListTouchEnd() {
    if (!pullRefreshRef.current.active) return;
    pullRefreshRef.current.active = false;
    if (pullDistance >= 44) {
      void refreshNews();
    } else {
      setPullDistance(0);
    }
  }

  function openArticle(article: ApiNewsArticle) {
    listScrollTopRef.current = viewRef.current?.scrollTop ?? 0;
    setSelectedArticle(article);
    requestAnimationFrame(() => {
      if (viewRef.current) viewRef.current.scrollTop = 0;
    });
  }

  function closeArticle() {
    pendingListScrollRestoreRef.current = listScrollTopRef.current;
    setSelectedArticle(null);
  }

  return (
    <section ref={viewRef} className={active ? 'news-view' : 'news-view news-view-hidden'} aria-label="Market news">
      {selectedArticle ? (
        <NewsDetail article={selectedArticle} onBack={closeArticle} />
      ) : (
        <div
          className="news-list-page"
          onTouchStart={handleListTouchStart}
          onTouchMove={handleListTouchMove}
          onTouchEnd={handleListTouchEnd}
        >
      <div className={refreshing || pullDistance > 0 ? 'news-pull-refresh active' : 'news-pull-refresh'} style={{ height: refreshing ? 44 : pullDistance }}>
        <span>{refreshing ? 'Refreshing news' : pullDistance >= 44 ? 'Release to refresh' : 'Pull to refresh'}</span>
      </div>
      <div className="news-sticky-header">
        <div className="news-filter-bar">
        <form className="news-search" role="search" onSubmit={submitSearch}>
          <Search size={16} />
          <input
            type="search"
            value={searchInput}
            placeholder="Search news, tickers, topics"
            onChange={(event) => setSearchInput(event.target.value)}
          />
          {searchQuery ? (
            <button type="button" aria-label="Clear news search" onClick={clearSearch}>
              <X size={15} />
            </button>
          ) : (
            <button type="submit" aria-label="Search news" disabled={!searchInput.trim()}>
              <ArrowRight size={15} />
            </button>
          )}
        </form>
        <div className="news-filter-scroll" role="list" aria-label="News filters">
          {filters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              className={activeFilter === filter.value ? 'news-filter active' : 'news-filter'}
              onClick={() => selectFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        </div>
      </div>

      <div className="news-list">
        {loading && <NewsSkeletonList />}
        {!loading && error && <NewsStatus label={error} tone="error" />}
        {!loading && !error && articles.length === 0 && <NewsStatus label="No news found." />}
        {!loading && !error && articles.map((article) => <NewsArticle key={article.id} article={article} onSelect={openArticle} />)}
        {loadingMore && <NewsSkeletonList count={2} />}
      </div>

      {!loading && !loadingMore && !error && hasMoreNews && articles.length > 0 && (
        <div className="news-load-more">
          <button type="button" onClick={() => void loadMoreNews()}>
            <span>Load more news</span>
            <ChevronDown size={18} />
          </button>
        </div>
      )}
        </div>
      )}
    </section>
  );
}

function appendNewArticles(current: ApiNewsArticle[], next: ApiNewsArticle[]): ApiNewsArticle[] {
  const seen = new Set(current.map((article) => article.id));
  const appended = next.filter((article) => !seen.has(article.id));
  return appended.length > 0 ? [...current, ...appended] : current;
}

function NewsArticle({ article, onSelect }: { article: ApiNewsArticle; onSelect(article: ApiNewsArticle): void }) {
  const sentiment = article.sentiment ?? 'neutral';
  const SentimentIcon = sentiment === 'positive' ? TrendingUp : sentiment === 'negative' ? TrendingDown : CircleMinus;
  const publishedAt = formatPublishedAt(article.published_at);
  const sourceName = article.source_name || article.attribution || 'News';
  const summary = article.summary || 'Open article for full coverage.';
  const tags = [article.entity_id, article.match_source].filter(Boolean);

  return (
    <article className="news-article">
      <button type="button" onClick={() => onSelect(article)}>
        <div className="news-article-meta">
          <div className="news-article-source">
            <span>{sourceName}</span>
            <time>{publishedAt}</time>
          </div>
          <span className={`news-sentiment ${sentiment}`}>
            <SentimentIcon size={13} />
            {sentiment}
          </span>
        </div>
        <h2>{article.title}</h2>
        <p>{summary}</p>
        <div className="news-tags">
          {tags.length > 0 ? tags.map((tag, index) => (
            <span key={tag}>
              {index === 0 ? <BadgeDollarSign size={14} /> : <Newspaper size={14} />}
              {tag}
            </span>
          )) : (
            <span><Newspaper size={14} />{sourceName}</span>
          )}
        </div>
      </button>
    </article>
  );
}

function NewsDetail({ article, onBack }: { article: ApiNewsArticle; onBack(): void }) {
  const sentiment = article.sentiment ?? 'neutral';
  const SentimentIcon = sentiment === 'positive' ? TrendingUp : sentiment === 'negative' ? TrendingDown : CircleMinus;
  const publishedAt = formatPublishedAt(article.published_at);
  const sourceName = article.source_name || article.attribution || 'News';
  const body = article.body_content || article.content || article.summary || 'This source did not provide article body content. Open the source for full coverage.';
  const swipeRef = useRef({ startX: 0, startY: 0, active: false });

  function handleTouchStart(event: TouchEvent<HTMLElement>) {
    const touch = event.touches[0];
    if (!touch || touch.clientX > 48) return;
    swipeRef.current = { startX: touch.clientX, startY: touch.clientY, active: true };
  }

  function handleTouchMove(event: TouchEvent<HTMLElement>) {
    if (!swipeRef.current.active) return;
    const touch = event.touches[0];
    if (!touch) return;
    const deltaX = touch.clientX - swipeRef.current.startX;
    const deltaY = Math.abs(touch.clientY - swipeRef.current.startY);
    if (deltaX > 36 && deltaX > deltaY * 1.4) {
      swipeRef.current.active = false;
      onBack();
    }
  }

  function handleTouchEnd() {
    swipeRef.current.active = false;
  }

  return (
    <article className="news-detail" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      <header className="news-detail-topbar">
        <button type="button" aria-label="Back to news" onClick={onBack}><ArrowLeft size={22} /></button>
        <span>News</span>
      </header>
      {article.image_url && <img className="news-detail-image" src={article.image_url} alt="" />}
      <div className="news-detail-body">
        <div className="news-article-meta">
          <div className="news-article-source">
            <span>{sourceName}</span>
            <time>{publishedAt}</time>
          </div>
          <span className={`news-sentiment ${sentiment}`}>
            <SentimentIcon size={13} />
            {sentiment}
          </span>
        </div>
        <h1>{article.title}</h1>
        {article.author && <p className="news-detail-author">By {article.author}</p>}
        <NewsBody content={body} format={article.body_content_format ?? article.content_format ?? 'text'} />
        <div className="news-tags">
          {[article.entity_id, article.match_source].filter(Boolean).map((tag, index) => (
            <span key={tag}>
              {index === 0 ? <BadgeDollarSign size={14} /> : <Newspaper size={14} />}
              {tag}
            </span>
          ))}
        </div>
        <a className="news-detail-source" href={article.url} target="_blank" rel="noreferrer">
          Open source
          <ExternalLink size={16} />
        </a>
      </div>
    </article>
  );
}

function NewsBody({ content, format }: { content: string; format: 'text' | 'markdown' }) {
  if (format !== 'markdown') {
    return <p className="news-detail-summary">{content}</p>;
  }

  const blocks = markdownBlocks(content);
  return (
    <div className="news-detail-markdown">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const Heading = block.level <= 2 ? 'h2' : 'h3';
          return <Heading key={index}>{renderMarkdownInline(block.text)}</Heading>;
        }
        if (block.type === 'list') {
          return (
            <ul key={index}>
              {block.items.map((item) => <li key={item}>{renderMarkdownInline(item)}</li>)}
            </ul>
          );
        }
        return <p key={index}>{renderMarkdownInline(block.text)}</p>;
      })}
    </div>
  );
}

function markdownBlocks(content: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  let listItems: string[] = [];
  for (const rawLine of content.split('\n')) {
    const line = rawLine.trim();
    if (!line) {
      if (listItems.length > 0) {
        blocks.push({ type: 'list', items: listItems });
        listItems = [];
      }
      continue;
    }
    const listMatch = line.match(/^[-*]\s+(.+)$/);
    if (listMatch?.[1]) {
      listItems.push(listMatch[1]);
      continue;
    }
    if (listItems.length > 0) {
      blocks.push({ type: 'list', items: listItems });
      listItems = [];
    }
    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading?.[1] && heading[2]) {
      if (blocks.length === 0 && heading[1] === '#') continue;
      blocks.push({ type: 'heading', text: heading[2], level: heading[1].length as 1 | 2 | 3 });
      continue;
    }
    blocks.push({ type: 'paragraph', text: line });
  }
  if (listItems.length > 0) blocks.push({ type: 'list', items: listItems });
  return blocks.length > 0 ? blocks : [{ type: 'paragraph', text: content }];
}

function renderMarkdownInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, index) => {
    const strong = part.match(/^\*\*(.+)\*\*$/);
    return strong?.[1] ? <strong key={index}>{strong[1]}</strong> : <Fragment key={index}>{part}</Fragment>;
  });
}

function NewsStatus({ label, tone }: { label: string; tone?: 'error' }) {
  return <div className={tone === 'error' ? 'news-status error' : 'news-status'}>{label}</div>;
}

function NewsSkeletonList({ count = 5 }: { count?: number }) {
  return (
    <div className="news-skeleton-list" aria-label="Loading news">
      {Array.from({ length: count }, (_, item) => <div className="news-skeleton-card" key={item} />)}
    </div>
  );
}

function formatPublishedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Latest';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}
